import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Moon, User, Phone, Loader2, CheckCircle } from 'lucide-react';
import { api } from '@/lib/api';

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [registrationOpen, setRegistrationOpen] = useState<boolean | null>(null);

  useEffect(() => {
    api.getRegistrationStatus().then((res) => {
      if (res.success && res.data) {
        setRegistrationOpen(res.data.open);
        if (!res.data.open) {
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    }).catch(() => {
      navigate('/', { replace: true });
    });
  }, [navigate]);
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.register({ ...formData, date_of_birth: today });
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(response.error || 'حدث خطأ أثناء التسجيل');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'حدث خطأ أثناء التسجيل');
    } finally {
      setLoading(false);
    }
  };

  if (registrationOpen === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="card max-w-md w-full text-center">
          <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-cream mb-2">تم التسجيل بنجاح!</h2>
          <p className="text-gray-400">جاري تحويلك لصفحة تسجيل الدخول...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/">
            <Moon className="w-12 h-12 text-gold-500 mx-auto mb-4" />
          </Link>
          <h1 className="text-3xl font-jazeera font-bold text-gold-500 mb-2">
            التسجيل في المسابقة
          </h1>
          <p className="text-gray-400">
            سجل الآن وكن جزءاً من رحلة عبر الزمن
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="card space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400 text-center">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              الاسم الكامل
            </label>
            <div className="relative">
              <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                required
                className="input-field pr-10"
                placeholder="أدخل اسمك الكامل"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              رقم الهاتف
            </label>
            <div className="relative">
              <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="tel"
                required
                className="input-field pr-10"
                placeholder="05XXXXXXXX"
                dir="ltr"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                جاري التسجيل...
              </>
            ) : (
              'سجل الآن'
            )}
          </button>

          <p className="text-center text-gray-400">
            لديك حساب بالفعل؟{' '}
            <Link to="/login" className="text-gold-500 hover:text-gold-400">
              تسجيل الدخول
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
